import { useState } from "react";
import { ShoppingCart, X, AlertTriangle, ChefHat } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";

/**
 * Pulsante "Ordina" da usare nelle card piatto: apre un dialog con porzioni
 * e ordina automaticamente la quantità esatta degli ingredienti necessari.
 */
export function OrderRecipeButton({ recipe }) {
    const [open, setOpen] = useState(false);
    return (
        <>
            <button
                type="button"
                data-testid={`order-recipe-btn-${recipe.id}`}
                onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setOpen(true);
                }}
                title="Ordina ingredienti per questo piatto"
                className="inline-flex items-center gap-1.5 rounded-full bg-brand-gold text-brand-navy text-[12px] font-bold px-3 py-1.5 hover:bg-brand-goldDark transition shadow-gold"
            >
                <ShoppingCart size={13} strokeWidth={2.4} />
                Ordina
            </button>
            {open && (
                <OrderPortionsDialog recipe={recipe} onClose={() => setOpen(false)} />
            )}
        </>
    );
}

function OrderPortionsDialog({ recipe, onClose }) {
    const [portions, setPortions] = useState(recipe.portions || 1);
    const [busy, setBusy] = useState(false);
    const [warn, setWarn] = useState("");
    const navigate = useNavigate();

    const submit = async (e) => {
        e?.preventDefault();
        const p = parseFloat(portions);
        if (!p || p <= 0) {
            setWarn("Inserisci un numero di porzioni maggiore di zero");
            return;
        }
        setBusy(true);
        setWarn("");
        try {
            const { data } = await api.post("/orders/cart/from-recipe", {
                recipe_id: recipe.id,
                portions: p,
            });
            const added = data.added || 0;
            const merged = Array.isArray(data.merged) ? data.merged : [];
            const skipped = data.skipped || [];
            const parts = [];
            if (added > 0) parts.push(`${added} nuovi ingredienti`);
            if (merged.length > 0) parts.push(`${merged.length} accorpati`);
            const summary = parts.join(", ") || "0 ingredienti";
            if (skipped.length > 0) {
                toast.warning(
                    `${summary} aggiunti al carrello`,
                    {
                        description: `Saltati: ${skipped.join(", ")}`,
                        action: {
                            label: "Vedi ordini",
                            onClick: () => navigate("/orders"),
                        },
                    }
                );
            } else {
                toast.success(
                    `${summary} per ${p} porzioni di ${recipe.name} aggiunti al carrello`,
                    {
                        action: {
                            label: "Vedi ordini",
                            onClick: () => navigate("/orders"),
                        },
                    }
                );
            }
            onClose();
        } catch (err) {
            setWarn(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-brand-navy/40 backdrop-blur-sm px-4"
            onClick={onClose}
            data-testid="order-portions-dialog"
        >
            <form
                onSubmit={submit}
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-md bg-paper rounded-2xl shadow-card border border-brand-line overflow-hidden"
            >
                <div className="px-6 py-4 border-b border-brand-line flex items-center justify-between gap-3">
                    <div>
                        <div className="eyebrow text-brand-gold flex items-center gap-1.5">
                            <ChefHat size={11} /> Ordine da ricetta
                        </div>
                        <h3 className="font-display text-2xl text-brand-ink mt-0.5 truncate">
                            {recipe.name}
                        </h3>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="h-9 w-9 rounded-full text-brand-muted hover:bg-brand-cream transition flex items-center justify-center shrink-0"
                    >
                        <X size={16} />
                    </button>
                </div>
                <div className="px-6 py-5 space-y-4">
                    <div>
                        <label className="block text-[10px] uppercase tracking-[0.16em] text-brand-muted mb-1.5">
                            Numero porzioni da preparare
                        </label>
                        <input
                            type="number"
                            step="1"
                            min="1"
                            value={portions}
                            autoFocus
                            onChange={(e) => setPortions(e.target.value)}
                            data-testid="order-portions-input"
                            className="w-full px-3 py-2.5 rounded-md bg-white border border-brand-line text-base focus:outline-none focus:border-brand-gold"
                        />
                        <div className="mt-1 text-[11px] text-brand-muted">
                            Ricetta base: <strong>{recipe.portions || 1} porzioni</strong> · gli
                            ingredienti verranno scalati proporzionalmente.
                        </div>
                    </div>
                    {warn && (
                        <div className="rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-[12px] px-3 py-2 flex items-start gap-2">
                            <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                            <span>{warn}</span>
                        </div>
                    )}
                </div>
                <div className="px-6 py-4 bg-brand-cream border-t border-brand-line flex items-center justify-end gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 rounded-full text-sm font-semibold text-brand-muted hover:text-brand-ink transition"
                    >
                        Annulla
                    </button>
                    <button
                        type="submit"
                        disabled={busy}
                        data-testid="order-portions-confirm"
                        className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-brand-navy text-white text-sm font-bold hover:bg-brand-navyLight transition disabled:opacity-60"
                    >
                        <ShoppingCart size={14} /> {busy ? "Aggiungo…" : "Aggiungi al carrello"}
                    </button>
                </div>
            </form>
        </div>
    );
}
